import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as monaco from "monaco-editor/esm/vs/editor/editor.api";
// Assumes react-markdown and remark-gfm are installed.
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Editor } from "@/components/editor/Monaco";
import { Button } from "@/components/ui/button";
import { PlusCircle, Save, Search } from "lucide-react";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { CodeShortcutBar } from "@/components/editor/CodeShortcutBar";
import { TabsBar } from "@/components/TabsBar/TabsBar";
import { TabsBarMobile } from "@/components/TabsBar/TabsBar.mobile";
import { useWorkspaceStore } from '@/stores/workspace';
import { useInstructionsStore } from '@/stores/instructions';
import { useFileOperations } from '@/hooks/workspace/useFileOperations';
import { toast } from 'sonner';
import { FindReplaceModal } from '@/components/editor/FindReplaceModal';
import { useUiStore } from '@/stores/ui';
import { useShallow } from 'zustand/react/shallow';
import { SaveNewFileDialog } from './SaveNewFileDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from 'react-router-dom';

export const CodeEditor = ({
  coverage,
  testFailures,
  coverageDesynced,
}: {
  coverage?: any;
  testFailures?: { line: number; message: string }[];
  coverageDesynced?: boolean;
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    tabs,
    updateTab,
    saveNewFile,
    openFileOptions,
    setOpenFileOptions,
    editedContent,
    openFilePath: activeTabId,
    pasteToTask
  } = useWorkspaceStore(state => ({
    tabs: state.tabs,
    updateTab: state.updateTab,
    openFileOptions: state.openFileOptions,
    setOpenFileOptions: state.setOpenFileOptions,
    saveNewFile: state.saveNewFile,
    openFilePath: state.openFilePath,
    editedContent: state.editedContent,
    pasteToTask: state.pasteToTask
  }));
  const activeTab = useMemo(() => tabs.find(tab => tab.id === activeTabId), [tabs, activeTabId]);
  const setInstructions = useInstructionsStore((state) => state.setInstructions);
  const previousContentRef = useRef<string>('');

  const { isFileExplorerSearchVisible, isGitPanelVisible } = useUiStore(useShallow(state => ({
    isFileExplorerSearchVisible: state.isFileExplorerSearchVisible,
    isGitPanelVisible: state.isGitPanelVisible,
  })));

  const { content, handleSaveFile, isSaving, handleContentChange } = useFileOperations();

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const [isFindReplaceOpen, setIsFindReplaceOpen] = useState(false);
  const [isSaveAsOpen, setIsSaveAsOpen] = useState(false);
  const activeTabIdRef = useRef<string | undefined>(activeTabId);
  activeTabIdRef.current = activeTabId;

  const onSave = useCallback(() => {
    if (activeTab?.isNew) {
      setIsSaveAsOpen(true);
    } else {
      handleSaveFile();
    }
  }, [activeTab, handleSaveFile]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 's' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onSave();
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [onSave]);

  useEffect(() => {
    // When the active tab changes, we should reset the previous content ref
    // to prevent the paste handler from misfiring. The editor's content will
    // be updated programmatically, which we don't want to interpret as a paste.
    previousContentRef.current = '';
  }, [activeTabId]);

  // This effect handles jumping to a line when the file is opened or changed.
  useEffect(() => {
    if (editorRef.current && openFileOptions?.line) {
      // Use a small timeout to ensure the model has been switched by the editor component
      setTimeout(() => {
        const editor = editorRef.current;
        // Check if the tab is still the active one before performing actions
        if (editor && activeTabIdRef.current === activeTabId) {
          const { line, character = 1 } = openFileOptions;
          editor.revealLineInCenter(line, monaco.editor.ScrollType.Smooth);
          editor.setPosition({ lineNumber: line, column: character });
          editor.focus();
          setOpenFileOptions(undefined); // Consume the options
        }
      }, 100);
    }
  }, [activeTabId, openFileOptions, setOpenFileOptions]);

  const isMarkdown = activeTabId?.toLowerCase().endsWith('.md');
  const [markdownViewMode, setMarkdownViewMode] = useState<'edit' | 'preview'>(isMarkdown ? 'preview' : 'edit');

  useEffect(() => {
    // Reset to edit mode when file changes
    setMarkdownViewMode(isMarkdown ? 'preview' : 'edit');
  }, [activeTabId, isMarkdown]);

  const extractMarkdownFromNode = (node: any): string => {
    if (node?.position?.start?.offset !== undefined && node.position.end?.offset !== undefined) {
      return content.substring(node.position.start.offset, node.position.end.offset);
    }
    return '';
  };

  const onEditorMount = (editor: monaco.editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;

    if (!isFileExplorerSearchVisible && !isGitPanelVisible) {
      editor.focus();
    }

    previousContentRef.current = editor.getValue();
    let skipNextChangeEvent = false;

    editor.onDidChangeModelContent(async (event) => {
      const model = editor.getModel();
      if (!model) return;
      
      const currentContent = editor.getValue();

      // Check for a single change that replaced the entire previous content
      if (
        activeTabId &&
        event.changes.length === 1 &&
        event.changes[0].rangeLength > 0 &&
        previousContentRef.current.length > 0 &&
        event.changes[0].rangeLength === previousContentRef.current.length &&
        !skipNextChangeEvent && currentContent.trim().length > 0
      ) {
        const pastedText = currentContent.trim();

          // Revert the change in the editor immediately to avoid flashing the diff.
          const revertOp = { range: model.getFullModelRange(), text: previousContentRef.current };
          editor.executeEdits('paste-handler-revert', [revertOp]);

          const toastId = toast.loading("Analyzing pasted content...");
          
          try {
            const response = await pasteToTask({
              markdownContent: pastedText,
              instructions: `Apply pasted content to ${activeTabId}`,
            });

            if (response?.action === 'task_created' && (response.filesChangedCount ?? 0) > 0) {
              toast.success("Proposal created from pasted content!", { id: toastId });
              navigate(`/pages/review/?taskId=${response.taskId}&proposalId=${response.proposalId}`);
            } else {
              skipNextChangeEvent = true;
              // No file changes detected by backend, or no action taken. Paste the text.
              if (response?.message) {
                toast.info(response.message, { id: toastId });
              } else {
                toast.info("Pasting content into editor.", { id: toastId });
              }
              const pasteOp = { range: model.getFullModelRange(), text: pastedText };
              editor.executeEdits('paste-handler-apply', [pasteOp]);

            }
          } catch (err: any) {
            toast.error("Failed to process paste.", { id: toastId, description: err.message });
            // On failure, still paste the content as a fallback.
            const pasteOp = { range: model.getFullModelRange(), text: pastedText };
            editor.executeEdits('paste-handler-fallback', [pasteOp]);
          }
          
          // Update ref to the latest content in the editor
          previousContentRef.current = editor.getValue();
          return; // Stop further processing for this event
      }
      skipNextChangeEvent = false;

      // If it wasn't a special paste, just update the previous content for the next event.
      previousContentRef.current = currentContent;
    });
  };

  const onFind = () => setIsFindReplaceOpen(true);

  const onInsert = (text: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = editor.getSelection();
    if (!selection) return;

    if (["()", "{}", "[]", '""', "''", "``"].includes(text)) {
      const op = {
        identifier: { major: 1, minor: 1 },
        range: selection,
        text: text[0] + editor.getModel()!.getValueInRange(selection) + text[1],
        forceMoveMarkers: true,
      };
      editor.executeEdits("shortcut-bar", [op], [new monaco.Selection(selection.startLineNumber, selection.startColumn + 1, selection.endLineNumber, selection.endColumn + 1)]);
    } else {
      editor.trigger("keyboard", "type", { text });
    }
    editor.focus();
  };

  const handleFindNext = (query: string) => { editorRef.current?.getModel()?.findNextMatch(query, editorRef.current.getPosition()!, false, true, null, true); };
  const handleReplace = (find: string, replace: string) => {
    const editor = editorRef.current;
    const model = editor?.getModel();
    const selection = editor?.getSelection();
    if (!editor || !model || !selection) return;

    if (model.getValueInRange(selection) === find) {
      editor.executeEdits("replace-action", [{ range: selection, text: replace }]);
    }
    handleFindNext(find);
  };
  const handleReplaceAll = (find: string, replace: string) => {
    const editor = editorRef.current;
    const model = editor?.getModel();
    if (!editor || !model) return;

    const matches = model.findMatches(find, true, false, true, null, true);
    const edits = matches.map((match) => ({ range: match.range, text: replace }));
    editor.executeEdits("replace-all-action", edits);
  };

  const onAppendToInstructions = useCallback((text: string) => {
      setInstructions((prev: string) => {
        if (prev.trim() === "") {
          return text;
        }
        return `${prev}\n\n${text}`;
      });
      toast.success("Added to instructions.");
    }, [setInstructions]
  );

  return (
    <>
      {activeTab?.isNew && (
        <SaveNewFileDialog
          isOpen={isSaveAsOpen}
          onClose={() => setIsSaveAsOpen(false)}
          onSave={async (newPath) => {
            try {
              if(activeTabId) {
                await saveNewFile(activeTabId, newPath);
              }
              setIsSaveAsOpen(false);
            } catch (e) {
              // error toast is handled in the action
            }
          }}
          fileName={activeTab.name}
          language={activeTab.language}
        />
      )}
      <FindReplaceModal
        isOpen={isFindReplaceOpen}
        onClose={() => setIsFindReplaceOpen(false)}
        onFindNext={handleFindNext}
        onReplace={handleReplace}
        onReplaceAll={handleReplaceAll}
      />
      <div className="h-full flex flex-col border rounded overflow-hidden">
        {activeTabId ? (
          <>
            {isMobile ? (
              <div className="shrink-0">
                <TabsBarMobile />
                <div className="p-1 border-b flex justify-between items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => onFind()}>
                    <Search className="h-3 w-3 mr-1" />
                    Find
                  </Button>
                  <div className="flex items-center gap-1">
                  {isMarkdown && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setMarkdownViewMode(prev => prev === 'edit' ? 'preview' : 'edit')}>
                      {markdownViewMode === 'edit' ? 'Preview' : 'Edit'}
                    </Button>
                  )}
                  {(editedContent[activeTabId] !== undefined || activeTab?.isNew) && (
                    <Button
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={onSave}
                      disabled={isSaving}
                    >
                      <Save className="h-3 w-3 mr-1" />{" "}
                      {isSaving ? "Saving..." : "Save"}
                    </Button>
                  )}
                  </div>
                </div>
              </div>
            ) : (
              <TabsBar />
            )}
            <div className="flex-1 min-h-0 relative" style={{
              backgroundColor: 'var(--vscode-editor-background)'
            }}>
              {isMarkdown && markdownViewMode === 'preview' ? (
                <div className="markdown-body max-w-none p-6 overflow-auto h-full">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      li: ({node, checked, children, ...props}: any) => {
                        const isTask = checked !== null;
                        return (
                          <li {...props} className="relative group">
                            {isTask && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="absolute -left-10 top-0 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity will-change-transform"
                                onClick={() => {
                                  const markdownText = extractMarkdownFromNode(node);
                                  if (markdownText) {
                                    const lines = markdownText.trim().split('\n');
                                    // This regex finds the list marker (e.g., "- "), captures any leading
                                    // markdown (like "**"), finds the checkbox ("[ ]"), and captures the rest
                                    // of the line. It then reconstructs the line without the marker and checkbox.
                                    lines[0] = lines[0].replace( /^\s*(?:[-*]|\d+\.)\s+(.*?)\s*\[[xX ]\]\s*(.*)/, '$1$2' );
                                    onAppendToInstructions(lines.join('\n'));
                                  }
                                }}
                                aria-label="Add to instructions"
                              >
                                <PlusCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {children}
                          </li>
                        );
                      }
                    }}>{content}</ReactMarkdown>
                </div>
              ) : (
                <Editor
                  path={activeTabId}
                  value={content}
                  language={activeTab?.language}
                  coverage={coverageDesynced ? undefined : coverage}
                  testFailures={coverage ? undefined : testFailures}
                  onMount={onEditorMount}
                  onChange={handleContentChange}
                />
              )}
              <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
                {!isMobile && activeTab?.isNew && (
                  <Select
                      value={activeTab.language}
                      onValueChange={(lang) => updateTab(activeTabId!, { language: lang })}
                  >
                      <SelectTrigger className="h-7 w-[120px] text-xs">
                          <SelectValue placeholder="Language" />
                      </SelectTrigger>
                      <SelectContent>
                          <SelectItem value="plaintext">Plain Text</SelectItem>
                          <SelectItem value="javascript">JavaScript</SelectItem>
                          <SelectItem value="typescript">TypeScript</SelectItem>
                          <SelectItem value="python">Python</SelectItem>
                          <SelectItem value="html">HTML</SelectItem>
                          <SelectItem value="css">CSS</SelectItem>
                          <SelectItem value="json">JSON</SelectItem>
                          <SelectItem value="markdown">Markdown</SelectItem>
                      </SelectContent>
                  </Select>
                )}
                {!isMobile && isMarkdown && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setMarkdownViewMode(prev => prev === 'edit' ? 'preview' : 'edit')}
                    >
                        {markdownViewMode === 'edit' ? 'Preview' : 'Edit'}
                    </Button>
                )}
                {!isMobile && (editedContent[activeTabId] !== undefined || activeTab?.isNew) && (
                  <Button
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={onSave}
                    disabled={isSaving}
                  >
                    <Save className="h-3 w-3 mr-1" />{" "}
                    {isSaving ? "Saving..." : `Save ${activeTab?.isNew ? 'As...' : ''} (Ctrl+S)`}
                  </Button>
                )}
              </div>
            </div>
            {isMobile && (
              <div className="shrink-0">
                <CodeShortcutBar onInsert={onInsert} />
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <p className="text-xs">
              Select a file to view its contents or press Ctrl+P to open a file.
            </p>
          </div>
        )}
      </div>
    </>
  );
};